import { Injectable, signal, WritableSignal } from '@angular/core';
import { Log } from '../models/log';
import { SupabaseDatabaseService } from './supabase-database.service';
import TYPES from '../constants/types';

@Injectable({
  providedIn: 'root'
})
export class LogService<T> extends SupabaseDatabaseService<T> {

  canAccessMindLogRegister: WritableSignal<boolean> = signal(false);

  log:WritableSignal<Log> = signal<Log>(
    {
      id: null,
      user_id: '',
      mood: '',
      feelings: '',
      body_reactions: '',
      what_happened: '',
      did_do: '',
      did_think: '',
      other_thought: '',
      created_at: null,
      updated_at: null,
      logs_shared: null
    }
  );

  async getAllLogsFromUser(userId: string): Promise<Log[]>{
    const { data, error } = await this.supabase
    .from(TYPES.DATABASE.TABLES.LOGS)
    .select(`
      id,
      user_id,
      mood,
      feelings,
      body_reactions,
      what_happened,
      did_do,
      did_think,
      other_thought,
      created_at,
      updated_at
    `)
    .eq('user_id', userId)
    .order('id', {ascending: false});

    if (error) {
      console.error(`Erro ao buscar LOGS em '${TYPES.DATABASE.TABLES.LOGS}':`,
        error);
      throw new Error(error.message);
    }
    return (data as Log[]) || [];

  }

  async getAllLogsFromUserPaginated(
    userId: string, 
    page: number,
    pageSize: number
  ): Promise<{logs: Log[], hasMore: boolean}>{

    const from = page * pageSize;
    const to = from + pageSize - 1;

    const { data, error, count } = await this.supabase
    .from(TYPES.DATABASE.TABLES.LOGS)
    .select(`
      id,
      user_id,
      mood,
      feelings,
      body_reactions,
      what_happened,
      did_do,
      did_think,
      other_thought,
      created_at,
      updated_at,
      logs_shared (
        id,
        psychologist_id,
        user_id,
        log_id,
        psychologists (
          first_name,
          last_name
        )
      )
    `,
    { count: 'exact' })
    .eq('user_id', userId)
    .order('id', {ascending: false})
    .range(from, to);

    if (error) {
      console.error(`Erro ao buscar LOGS em '${TYPES.DATABASE.TABLES.LOGS}':`,
        error);
      throw new Error(error.message);
    }

    const newLogs:Log[] = (data as unknown as Log[]) || [];
    const totalCount:number = count || 0;
    const hasMore:boolean = from + pageSize < totalCount

    return {
      logs: newLogs, hasMore: hasMore
    };

  }

  async getAllLogsSharedWithUser(userId: string): Promise<any[]>{
    const { data, error } = await this.supabase
    .from(TYPES.DATABASE.TABLES.LOGS_SHARED)
    .select(`
      id,
      users!logs_shared_user_id_fkey!inner (
        id,
        first_name,
        last_name
      ),
      logs!inner (
        id,
        user_id,
        mood,
        feelings,
        body_reactions,
        what_happened,
        did_do,
        did_think,
        other_thought,
        created_at,
        updated_at
      ),
      psychologists!inner (
        id,
        user_id
      )
    `)
    .eq('psychologists.user_id', userId)
    .order('id', {ascending: false});

    if (error) {
      console.error(`Erro ao buscar LOGS em '${TYPES.DATABASE.TABLES.LOGS_SHARED}':`,
        error);
      throw new Error(error.message);
    }
    return (data as any[]) || [];
  }

  async getLogsSharedWithUserForStarterPlan(
    psychologistId:string,
    userId: string
  ): Promise<any[]>{
    const { data, error } = await this.supabase
    .from(TYPES.DATABASE.TABLES.LOGS_SHARED)
    .select(`
      id,
      users!logs_shared_user_id_fkey!inner (
        id,
        first_name,
        last_name
      ),
      logs!inner (
        id,
        user_id,
        mood,
        feelings,
        body_reactions,
        what_happened,
        did_do,
        did_think,
        other_thought,
        created_at,
        updated_at
      ),
      psychologists!inner (
        id,
        user_id
      )
    `)
    .eq('user_id', userId)
    .eq('psychologist_id', psychologistId)
    .order('id', {ascending: true})
    .limit(10);

    if (error) {
      console.error(`Erro ao buscar LOGS em '${TYPES.DATABASE.TABLES.LOGS_SHARED}':`,
        error);
      throw new Error(error.message);
    }
    return (data as any[]) || [];
  }

  async getAllLogsSharedWithPsychologistByUserPaginated(
    psychologistId:string, 
    userId:string,
    page:number,
    pageSize: number
  ): Promise<{logs: any[], hasMore: boolean}>{
    
    const from = page * pageSize;
    const to = from + pageSize - 1;

    const { data, error, count } = await this.supabase
    .from(TYPES.DATABASE.TABLES.LOGS_SHARED)
    .select(`
      id,
      users!logs_shared_user_id_fkey!inner (
        id,
        first_name,
        last_name
      ),
      logs!inner (
        id,
        user_id,
        mood,
        feelings,
        body_reactions,
        what_happened,
        did_do,
        did_think,
        other_thought,
        created_at,
        updated_at
      ),
      psychologists!inner (
        id,
        user_id
      )
    `,
    { count: 'exact' })
    .eq('user_id', userId)
    .eq('psychologist_id', psychologistId)
    .order('logs(id)', {ascending: false})
    .range(from, to);

    if (error) {
      console.error(`Erro ao buscar LOGS em '${TYPES.DATABASE.TABLES.LOGS_SHARED}':`,
        error);
      throw new Error(error.message);
    }

    const logsShared:Log[] = (data as unknown as Log[]) || [];
    const totalCount:number = count || 0;
    const hasMore:boolean = from + pageSize < totalCount

    return {
      logs: logsShared, hasMore: hasMore
    };
  }

  async getUsersNameSharingLogsByPsychologistId(psychologistId: string): Promise<any[]>{
    const { data, error } = await this.supabase
    .from(TYPES.DATABASE.TABLES.LOGS_SHARED)
    .select(`
      id,
      users!logs_shared_user_id_fkey!inner (
        id,
        first_name,
        last_name
      )
    `)
    .eq('psychologist_id', psychologistId)
    .order('id', {ascending: false});

    if (error) {
      console.error(`Erro ao buscar users em '${TYPES.DATABASE.TABLES.LOGS_SHARED}':`,
        error);
      throw new Error(error.message);
    }
    return (data as any[]) || [];
  }

  async getUsersNameSharingLogsByPsychologistIdForStarterPlan(psychologistId: string): Promise<any[]>{
    const { data, error } = await this.supabase
    .from(TYPES.DATABASE.TABLES.LOGS_SHARED)
    .select(`
      id,
      users!logs_shared_user_id_fkey!inner (
        id,
        first_name,
        last_name
      )
    `)
    .eq('psychologist_id', psychologistId)
    .order('id', {ascending: true})
    .limit(2);

    if (error) {
      console.error(`Erro ao buscar users em '${TYPES.DATABASE.TABLES.LOGS_SHARED}':`,
        error);
      throw new Error(error.message);
    }
    return (data as any[]) || [];
  }

  async hasReachedLimit(psychologistId: string) {
    const MAX_STARTER_LIMIT = 10;

    const { count, error } = await this.supabase
      .from(TYPES.DATABASE.TABLES.LOGS_SHARED)
      .select("*", { count: "exact", head: true })
      .eq("psychologist_id", psychologistId);

    if (error) {
      console.error("Erro ao contar logs:", error);
      throw error;
    }

    return (count ?? 0) >= MAX_STARTER_LIMIT;
  }


  resetLogState(){
    this.log.set(
      {
        id: null,
        user_id: '',
        mood: '',
        feelings: '',
        body_reactions: '',
        what_happened: '',
        did_do: '',
        did_think: '',
        other_thought: '',
        created_at: null,
        updated_at: null,
        logs_shared: null
      }
    );
  }

}
